import React__default, { useRef, useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { childrenToText, useClickOutside, focusedElement } from '../../utils.js';
import useTypeahead, { KEY_DOWN, KEY_UP, KEY_ESC } from './use-typeahead.js';
import DropdownContext from './context.js';
import styles from 'ui-components/Dropdown.scss';

var _excluded = ["className", "placeholder", "variant", "disabled"];
var Chevron = function Chevron(_ref) {
  var className = _ref.className;
  return /*#__PURE__*/React__default.createElement("svg", {
    className: className,
    height: "6.36377",
    viewBox: "0 0 9.89941 6.36377",
    width: "9.89941",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React__default.createElement("path", {
    d: "m4.95 6.364-4.95-4.95 1.414-1.414 3.536 3.536 3.535-3.536 1.414 1.414z",
    fill: "currentColor"
  }));
};
var Dropdown = function Dropdown(_ref2) {
  var _cn, _cn2, _cn3;
  var className = _ref2.className,
    _ref2$placeholder = _ref2.placeholder,
    placeholder = _ref2$placeholder === void 0 ? "Select an option…" : _ref2$placeholder,
    variant = _ref2.variant,
    disabled = _ref2.disabled,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded);
  var element = useRef(null);
  var _useState = useState(false),
    open = _useState[0],
    setOpen = _useState[1];
  var _useState2 = useState(false),
    visible = _useState2[0],
    setVisbile = _useState2[1];
  var _useState3 = useState("Down"),
    direction = _useState3[0],
    setDirection = _useState3[1];
  var items = React__default.Children.toArray(props.children).map(function (x, i) {
    return {
      index: i,
      value: x.props.value,
      label: childrenToText(x.props.children)
    };
  });
  useEffect(function () {
    if (disabled === true) {
      closeDropdown();
    }
  }, [disabled]);
  var closeDropdown = function closeDropdown() {
    setOpen(false);
    setVisbile(false);
  };
  var openDropdown = function openDropdown() {
    if (disabled === true) {
      return;
    }
    setVisbile(false);
    setOpen(true);
  };

  // Calc height after opened
  useEffect(function () {
    var calcDirection = function calcDirection(element) {
      var main = element.querySelector("main");
      if (!main || !document.documentElement) {
        return;
      }
      var screenHeight = document.documentElement ? document.documentElement.clientHeight : 0;
      var dropdownRect = element.getBoundingClientRect();
      var menuHeight = main.clientHeight;
      var spaceAtTheBottom = screenHeight - dropdownRect.top - dropdownRect.height - menuHeight;
      var spaceAtTheTop = dropdownRect.top - menuHeight;
      var upward = spaceAtTheBottom < 0 && spaceAtTheTop > spaceAtTheBottom;

      // Set state only if there's a relevant difference
      if (upward && direction !== "Up") {
        setDirection("Up");
      }
      if (!upward && direction !== "Down") {
        setDirection("Down");
      }
    };
    if (element.current && open) {
      calcDirection(element.current);
      setVisbile(true);
    }
  }, [open, direction]);
  var focusAtIndex = useCallback(function (index) {
    var item = items.find(function (x) {
      return x.index === index;
    });
    if (index === null && document.activeElement) {
      document.activeElement.blur();
    }
    if (element.current && item && item.value) {
      var itemElement = element.current.querySelector("[data-value=\"" + item.value + "\"]");
      if (itemElement) {
        itemElement.focus();
      }
    }
  }, [element, items]);
  var _useState4 = useState(""),
    typeaheadValue = _useState4[0],
    setTypeaheadValue = _useState4[1];
  useTypeahead(element.current, typeaheadValue, function (str) {
    setTypeaheadValue(str);
    if (!str) {
      focusAtIndex(null);
      return;
    }
    var foundItem = items.filter(function (item) {
      return item.label.slice(0, str.length) === str.toLowerCase();
    })[0] || items[0];
    if (foundItem) {
      focusAtIndex(foundItem.index);
    }
  });
  useClickOutside(element, function () {
    return closeDropdown();
  });
  var onSelect = function onSelect(id) {
    props.onChange(id);
    closeDropdown();
  };
  useEffect(function () {
    var current = element.current;
    var focusPrev = function focusPrev(element) {
      if (!element) {
        return;
      }
      var index = parseInt(element.getAttribute("data-index"), 10);
      focusAtIndex(Number.isNaN(index) ? 0 : index - 1);
    };
    var focusNext = function focusNext(element) {
      if (!element) {
        return;
      }
      var index = parseInt(element.getAttribute("data-index"), 10);
      focusAtIndex(Number.isNaN(index) ? 0 : index + 1);
    };
    if (current) {
      var onKeyDown = function onKeyDown(e) {
        switch (e.which) {
          case KEY_ESC:
            return closeDropdown();
          case KEY_UP:
            return focusPrev(focusedElement(current));
          case KEY_DOWN:
            return focusNext(focusedElement(current));
        }
      };
      current.addEventListener("keydown", onKeyDown);
      return function () {
        return current.removeEventListener("keydown", onKeyDown);
      };
    }
  }, [element, focusAtIndex]);
  var selectedChild = Array.isArray(props.children) && props.children.find(function (x) {
    return x.props.value === props.value;
  });
  var selectedInner = selectedChild && selectedChild.props.children;
  var buttonText = selectedInner ? childrenToText(selectedInner) : placeholder;
  return /*#__PURE__*/React__default.createElement("div", {
    ref: element,
    "data-open": open ? "true" : "false",
    className: cn(styles.block, className, (_cn = {}, _cn[styles.blockOpen] = open, _cn), [styles["block" + direction]], (_cn2 = {}, _cn2[styles.hasVariant] = Boolean(variant), _cn2), (_cn3 = {}, _cn3[styles[variant]] = Boolean(variant), _cn3))
  }, /*#__PURE__*/React__default.createElement("button", {
    disabled: disabled === true,
    type: "button",
    "aria-label": buttonText,
    className: styles.head,
    onClick: function onClick() {
      if (open) {
        closeDropdown();
      } else {
        openDropdown();
      }
    }
  }, /*#__PURE__*/React__default.createElement("div", {
    className: styles.headInner
  }, selectedInner || placeholder), /*#__PURE__*/React__default.createElement("div", {
    className: styles.chevronArea
  }, /*#__PURE__*/React__default.createElement(Chevron, {
    className: styles.chevron
  }))), open && /*#__PURE__*/React__default.createElement("main", {
    className: styles.body,
    style: {
      visibility: visible ? "visible" : "hidden"
    }
  }, /*#__PURE__*/React__default.createElement(DropdownContext.Provider, {
    value: {
      onSelect: onSelect,
      value: props.value,
      styles: styles,
      items: items
    }
  }, props.children)));
};

export default Dropdown;
//# sourceMappingURL=basic.js.map
